import { isLeadCaptureFlagEnabled } from './leadCaptureGate';

export type CheckoutProduct = {
  sku: string;
  slug: string;
  name: string;
  category: string;
  brand: string;
  priceCents: number;
  currency: string;
  shippingClass: string;
  checkoutEligible: boolean;
  availability: 'pilot_available' | 'not_available_online';
};

export type CheckoutCatalog = {
  checkoutMode: 'disabled' | 'test' | 'live';
  publicCheckoutEnabled: boolean;
  products: CheckoutProduct[];
};

export type CartItem = {
  sku: string;
  name: string;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
  currency: string;
  checkoutEligible: boolean;
};

export type CartPayload = {
  id: string;
  email: string | null;
  status: string;
  items: CartItem[];
  totalCents: number;
  currency: string;
};

export type OrderStatusPayload = {
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  updated_at: string;
};

type ApiErrorPayload = {
  error?: string;
  message?: string;
};

export class CommerceApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message || 'De aanvraag kon niet worden verwerkt.');
    this.name = 'CommerceApiError';
    this.status = status;
    this.code = payload.error;
  }
}

const apiBase = (import.meta.env.VITE_COMMERCE_API_BASE_URL ?? '').replace(/\/$/, '');
const leadCaptureEnabled = isLeadCaptureFlagEnabled(import.meta.env.VITE_LEAD_CAPTURE_ENABLED);

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {})
    }
  });
  const text = await response.text();
  const payload = text ? safeJson(text) : {};
  if (!response.ok) {
    throw new CommerceApiError(response.status, payload);
  }
  return payload as T;
}

function safeJson(text: string): ApiErrorPayload {
  try {
    return JSON.parse(text) as ApiErrorPayload;
  } catch {
    return { message: 'De webshopservice is tijdelijk niet bereikbaar.' };
  }
}

export function formatApiPrice(cents: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function getCheckoutCatalog() {
  return apiRequest<CheckoutCatalog>('/api/checkout/catalog');
}

export type NewsletterLeadReceipt = {
  id: string;
  status: string;
  created: boolean;
  analytics: {
    event: 'sign_up';
    eventId: string;
    tenantId: 'propickleball';
  };
};

export function submitNewsletterLead(input: {
  email: string;
  interest: string;
  consent: true;
  idempotencyKey: string;
  source?: string;
}) {
  if (!leadCaptureEnabled) {
    return Promise.reject(new CommerceApiError(503, {
      error: 'lead_capture_disabled',
      message: 'Online leadopslag is nog niet vrijgegeven.'
    }));
  }
  return apiRequest<NewsletterLeadReceipt>('/api/leads/newsletter', {
    method: 'POST',
    body: JSON.stringify(input)
  });
}

export function submitClubQuoteLead(input: {
  organization: string;
  email: string;
  players: number;
  surface: 'indoor' | 'outdoor' | 'mixed';
  budget: 'starter' | 'balanced' | 'premium';
  consent: true;
  source?: string;
}) {
  if (!leadCaptureEnabled) {
    return Promise.reject(new CommerceApiError(503, {
      error: 'lead_capture_disabled',
      message: 'Online leadopslag is nog niet vrijgegeven.'
    }));
  }
  return apiRequest<{ id: string; status: string }>('/api/leads/club-quote', {
    method: 'POST',
    body: JSON.stringify(input)
  });
}

export function createCart(input: { email?: string } = {}) {
  return apiRequest<{ id: string; status: string }>('/api/cart', {
    method: 'POST',
    body: JSON.stringify(input)
  });
}

export function getCart(cartId: string) {
  return apiRequest<CartPayload>(`/api/cart/${encodeURIComponent(cartId)}`);
}

export function addCartItem(cartId: string, sku: string, quantity: number) {
  return apiRequest<{ cartId: string; sku: string; quantity: number }>(`/api/cart/${encodeURIComponent(cartId)}/items`, {
    method: 'POST',
    body: JSON.stringify({ sku, quantity })
  });
}

export function updateCartItem(cartId: string, sku: string, quantity: number) {
  return apiRequest<{ cartId: string; sku: string; quantity: number }>(`/api/cart/${encodeURIComponent(cartId)}/items/${encodeURIComponent(sku)}`, {
    method: 'PATCH',
    body: JSON.stringify({ sku, quantity })
  });
}

export function removeCartItem(cartId: string, sku: string) {
  return apiRequest<void>(`/api/cart/${encodeURIComponent(cartId)}/items/${encodeURIComponent(sku)}`, {
    method: 'DELETE'
  });
}

export function startCheckout(input: {
  cartId: string;
  customer: { email: string; name?: string; phone?: string };
  privacyAccepted: true;
}) {
  return apiRequest<{ orderNumber: string; checkoutUrl?: string; paymentId: string }>('/api/checkout/session', {
    method: 'POST',
    body: JSON.stringify(input)
  });
}

export function getOrderStatus(orderNumber: string) {
  return apiRequest<OrderStatusPayload>(`/api/orders/${encodeURIComponent(orderNumber)}/status`);
}

export function requestReturn(input: { orderNumber: string; email: string; reason: string }) {
  return apiRequest<{ status: string }>('/api/returns/request', {
    method: 'POST',
    body: JSON.stringify(input)
  });
}
